/**
 * "My sites": everything the user published from Work to
 * <name>.sites.gabusercontent.com, with status, expiry and actions.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { View, StyleSheet, ScrollView, ActivityIndicator } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/context/ThemeContext';
import { sitesApi } from '@/lib/api';
import { slimPublishedSite } from '@/lib/publishedSites';
import Text from '@/components/Typography/Text';
import MetaHead from '@/components/MetaHead';
import { PublishedSiteCard } from '@/components/Coworker/WorkPublishedSites';

export default function PublishedSitesPage() {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const [state, setState] = useState({ loading: true, sites: [], limits: null, error: null });

  const load = useCallback(async () => {
    try {
      const out = await sitesApi.list();
      setState({ loading: false, sites: (out?.sites || []).map(slimPublishedSite).filter(Boolean), limits: out?.limits || null, error: null });
    } catch (error) {
      setState((s) => ({ ...s, loading: false, error: error?.message || 'Could not load your sites' }));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const live = state.sites.filter((s) => s.status === 'live' && (!s.expiresAt || Date.parse(s.expiresAt) > Date.now())).length;

  return (
    <ScrollView style={{ flex: 1, backgroundColor: colors.solid_color_primary }} contentContainerStyle={[styles.content, { paddingBottom: insets.bottom + 40 }]}>
      <MetaHead title="My sites" />
      <View style={styles.header}>
        <Text size="xl" weight="bold">My sites</Text>
        <Text size="sm" color="secondary">
          Pages and games Work published for you at a public link.
          {state.limits ? ` Your plan keeps each site online for ${state.limits.ttlDays} days, with up to ${state.limits.maxLive} live at once (${live} live now).` : ''}
          {' '}Ask Work to publish something you made, or to update or take down a site.
        </Text>
      </View>
      {state.loading ? <ActivityIndicator style={styles.spinner} color={colors.text_color_secondary} /> : null}
      {state.error ? <Text size="sm" color="secondary">{state.error}</Text> : null}
      {!state.loading && !state.error && !state.sites.length ? (
        <View style={[styles.empty, { borderColor: colors.border_color_secondary }]}>
          <Text size="sm" weight="medium">No published sites yet</Text>
          <Text size="sm" color="secondary">After Work builds a game or page, ask it to publish it and pick a name.</Text>
        </View>
      ) : null}
      <View style={styles.list}>
        {state.sites.map((site) => <PublishedSiteCard key={site.name} site={site} onUnpublished={() => { void load(); }} />)}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  content: { width: '100%', maxWidth: 720, alignSelf: 'center', padding: 24, gap: 16 },
  header: { gap: 6 },
  list: { gap: 10 },
  empty: { borderWidth: 1, borderRadius: 14, padding: 16, gap: 4 },
  spinner: { marginTop: 24 },
});
